import clsx from 'clsx'
import { Input } from 'primitives/input'
import { useEffect, useRef, useState } from 'react'
import { useDebouncedValue } from '../../hooks/use-debounded-values'
import type { DataTableInstance, DataTableRowData } from '../../types'
import { parseFromValuesOrFunc } from '../../utils/utils'

interface Props<TData extends DataTableRowData> extends React.ComponentProps<typeof Input> {
  table: DataTableInstance<TData>
}

export const GlobalFilterTextInput = <TData extends DataTableRowData>({
  table,
  ...rest
}: Props<TData>) => {
  const {
    getState,
    options: { localization, cxTableSearchTextInputProps, manualFiltering },
    setGlobalFilter,
  } = table
  const { globalFilter } = getState()

  const textFieldProps = {
    ...parseFromValuesOrFunc(cxTableSearchTextInputProps, {
      table,
    }),
    ...rest,
  }

  const isMounted = useRef(false)
  const [searchValue, setSearchValue] = useState(globalFilter ?? '')

  const [debouncedSearchValue] = useDebouncedValue(searchValue, manualFiltering ? 500 : 250)

  useEffect(() => {
    setGlobalFilter(debouncedSearchValue || undefined)
  }, [debouncedSearchValue, setGlobalFilter])

  const handleClear = () => {
    setSearchValue('')
    setGlobalFilter(undefined)
  }

  useEffect(() => {
    if (isMounted.current) {
      if (globalFilter === undefined) {
        handleClear()
      } else {
        setSearchValue(globalFilter)
      }
    }
    isMounted.current = true
  }, [globalFilter])

  const placeholder = textFieldProps?.placeholder || localization.search

  return (
    <Input
      placeholder={placeholder}
      {...textFieldProps}
      value={searchValue ?? ''}
      onChange={(event) => setSearchValue(event.target.value)}
      className={clsx(
        'mrt-global-filter-text-input relative z-10 font-inter text-s-400 normal-case tracking-normal',
        textFieldProps?.className,
      )}
    />
  )
}
